import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "react-bootstrap";
import Chart from "chart.js/auto";
import { ApiHook } from "../../../hooks/apiHook";
import DateRangeFilter from "./DateRangeFilter";
import LegFilter from "./LegFilter";

const SalesTrendChart = ({ trend, t }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !trend || trend.length === 0) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = chartRef.current.getContext("2d");
    chartInstance.current = new Chart(ctx, {
      type: "line",
      data: {
        labels: trend.map((t) => t.date),
        datasets: [
          {
            label: t("groupSales"),
            data: trend.map((t) => t.groupSales),
            borderColor: "#4e73df",
            backgroundColor: "rgba(78, 115, 223, 0.1)",
            fill: true,
            tension: 0.3,
          },
          {
            label: t("directSales"),
            data: trend.map((t) => t.directSales),
            borderColor: "#1cc88a",
            backgroundColor: "rgba(28, 200, 138, 0.1)",
            fill: true,
            tension: 0.3,
          },
          {
            label: t("personalPurchases"),
            data: trend.map((t) => t.personalPurchases),
            borderColor: "#f6c23e",
            backgroundColor: "rgba(246, 194, 62, 0.1)",
            fill: true,
            tension: 0.3,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "top", labels: { font: { size: 11 } } },
        },
        scales: {
          x: { ticks: { font: { size: 10 }, maxTicksLimit: 15 } },
          y: { beginAtZero: true, ticks: { font: { size: 10 } } },
        },
      },
    });

    return () => {
      if (chartInstance.current) chartInstance.current.destroy();
    };
  }, [trend, t]);

  return (
    <div style={{ height: "300px" }}>
      <canvas ref={chartRef}></canvas>
    </div>
  );
};

const SalesByLegChart = ({ salesByLeg }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !salesByLeg || salesByLeg.length === 0) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const topLegs = salesByLeg.slice(0, 10);
    const ctx = chartRef.current.getContext("2d");
    chartInstance.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: topLegs.map((l) => l.username),
        datasets: [
          {
            label: "Group Sales",
            data: topLegs.map((l) => l.groupSales),
            backgroundColor: "#4e73df",
          },
          {
            label: "Direct Sales",
            data: topLegs.map((l) => l.directSales),
            backgroundColor: "#1cc88a",
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "top", labels: { font: { size: 11 } } },
        },
        scales: {
          x: { ticks: { font: { size: 10 } } },
          y: { beginAtZero: true, ticks: { font: { size: 10 } } },
        },
      },
    });

    return () => {
      if (chartInstance.current) chartInstance.current.destroy();
    };
  }, [salesByLeg]);

  return (
    <div style={{ height: "300px" }}>
      <canvas ref={chartRef}></canvas>
    </div>
  );
};

const SalesAnalyticsSection = ({ legs }) => {
  const { t } = useTranslation();

  const today = useMemo(() => new Date(), []);
  const firstDayOfMonth = useMemo(
    () => new Date(today.getFullYear(), today.getMonth(), 1),
    [today]
  );
  const [startDate, setStartDate] = useState(
    firstDayOfMonth.toISOString().slice(0, 10)
  );
  const [endDate, setEndDate] = useState(today.toISOString().slice(0, 10));
  const [selectedLegId, setSelectedLegId] = useState("");

  const startDateISO = useMemo(() => `${startDate}T00:00:00`, [startDate]);
  const endDateISO = useMemo(() => `${endDate}T23:59:59`, [endDate]);

  const { data, isLoading, isFetching, isError } = ApiHook.CallNetworkSalesAnalytics(
    startDateISO,
    endDateISO,
    selectedLegId || null
  );

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" size="sm" /> {t("loading")}
      </div>
    );
  }

  if (isError || !data?.data) {
    return <div className="alert alert-warning">{t("errorLoadingData")}</div>;
  }

  const { periodSummary, salesByLeg, trend, directSalesDetail } = data.data;

  const formatNum = (n) => {
    if (n == null || isNaN(n)) return "0";
    return Number(n).toLocaleString();
  };

  const formatDate = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div>
      {/* Date Range + Leg Filter */}
      <div className="row mb-3 mt-3 align-items-end">
        <div className="col-md-8">
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            showAllTime
          />
        </div>
        <div className="col-md-4">
          <LegFilter
            legs={legs || []}
            selectedLegId={selectedLegId}
            setSelectedLegId={setSelectedLegId}
          />
        </div>
      </div>
      {isFetching && (
        <Spinner animation="border" size="sm" className="ms-2 mb-2" />
      )}

      {/* Sales Summary Cards */}
      <div className="row mb-3">
        <div className="col-lg-4 col-md-4 col-sm-6 mb-3">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-body text-center">
              <h6 className="text-muted mb-1" style={{ fontSize: "12px" }}>{t("directSales")}</h6>
              <h4 className="mb-0" style={{ color: "#1cc88a", fontWeight: "bold" }}>
                {formatNum(periodSummary.directSales)}
              </h4>
            </div>
          </div>
        </div>
        <div className="col-lg-4 col-md-4 col-sm-6 mb-3">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-body text-center">
              <h6 className="text-muted mb-1" style={{ fontSize: "12px" }}>{t("groupSales")}</h6>
              <h4 className="mb-0" style={{ color: "#4e73df", fontWeight: "bold" }}>
                {formatNum(periodSummary.groupSales)}
              </h4>
            </div>
          </div>
        </div>
        <div className="col-lg-4 col-md-4 col-sm-6 mb-3">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-body text-center">
              <h6 className="text-muted mb-1" style={{ fontSize: "12px" }}>{t("personalPurchases")}</h6>
              <h4 className="mb-0" style={{ color: "#f6c23e", fontWeight: "bold" }}>
                {formatNum(periodSummary.personalPurchases)}
              </h4>
            </div>
          </div>
        </div>
      </div>

      {/* Sales Trend Chart */}
      <div className="row">
        <div className="col-md-7 mb-3">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <h6 className="card-title" style={{ fontSize: "14px" }}>{t("salesTrend")}</h6>
              {trend && trend.length > 0 ? (
                <SalesTrendChart trend={trend} t={t} />
              ) : (
                <p className="text-muted text-center py-4">{t("noDataAvailable")}</p>
              )}
            </div>
          </div>
        </div>
        <div className="col-md-5 mb-3">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <h6 className="card-title" style={{ fontSize: "14px" }}>{t("salesByLeg")}</h6>
              {salesByLeg && salesByLeg.length > 0 ? (
                <SalesByLegChart salesByLeg={salesByLeg} />
              ) : (
                <p className="text-muted text-center py-4">{t("noDataAvailable")}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sales by Leg Table */}
      {salesByLeg && salesByLeg.length > 0 && (
        <div className="card shadow-sm border-0 mb-3">
          <div className="card-body">
            <h6 className="card-title" style={{ fontSize: "14px" }}>{t("salesByLegDetails")}</h6>
            <div className="table-responsive">
              <table className="table table-sm table-hover" style={{ fontSize: "12px" }}>
                <thead>
                  <tr>
                    <th>{t("legLabel")}</th>
                    <th>{t("username")}</th>
                    <th className="text-end">{t("groupSales")}</th>
                    <th className="text-end">{t("directSales")}</th>
                    <th className="text-end">{t("personalPurchases")}</th>
                  </tr>
                </thead>
                <tbody>
                  {salesByLeg.map((leg) => (
                    <tr key={leg.legId}>
                      <td>{t("legLabel")} {leg.legPosition}</td>
                      <td>{leg.username}</td>
                      <td className="text-end">{formatNum(leg.groupSales)}</td>
                      <td className="text-end">{formatNum(leg.directSales)}</td>
                      <td className="text-end">{formatNum(leg.personalPurchases)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Direct Sales Details Table */}
      {directSalesDetail && directSalesDetail.length > 0 && (
        <div className="card shadow-sm border-0">
          <div className="card-body">
            <h6 className="card-title" style={{ fontSize: "14px" }}>{t("directSalesDetails")}</h6>
            <div className="table-responsive">
              <table className="table table-sm table-hover" style={{ fontSize: "12px" }}>
                <thead>
                  <tr>
                    <th>{t("date")}</th>
                    <th>{t("buyer")}</th>
                    <th className="text-end">{t("saleValue")}</th>
                    <th className="text-end">{t("purchaseAmount")}</th>
                    <th>{t("saleType")}</th>
                  </tr>
                </thead>
                <tbody>
                  {directSalesDetail.map((row) => (
                    <tr key={row.saleId}>
                      <td className="text-nowrap">{formatDate(row.date)}</td>
                      <td>
                        {row.buyer ? (
                          <>
                            <strong>{row.buyer.username}</strong>
                            {row.buyer.fullName && (
                              <div className="text-muted" style={{ fontSize: "11px" }}>
                                {row.buyer.fullName}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-end">
                        <strong style={{ color: "#1cc88a" }}>{formatNum(row.saleValue)}</strong>
                      </td>
                      <td className="text-end">{formatNum(row.purchaseAmount)}</td>
                      <td>
                        {row.saleType && (
                          <span
                            className="badge"
                            style={{
                              backgroundColor: row.saleType === "migration" ? "#858796" : "#36b9cc",
                              color: "#fff",
                              fontSize: "10px",
                            }}
                          >
                            {row.saleType}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesAnalyticsSection;
